import { useCallback, useEffect, useState } from 'react';
import Cropper, { type Area } from 'react-easy-crop';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  HStack,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

interface CoverImageCropperProps {
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
  onCropped: (file: File) => void;
  isSaving?: boolean;
}

function createImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener('load', () => resolve(img));
    img.addEventListener('error', reject);
    img.src = url;
  });
}

async function cropToBlob(imageSrc: string, area: Area): Promise<Blob> {
  const image = await createImage(imageSrc);
  const size = Math.max(1, Math.round(area.width));
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(image, area.x, area.y, area.width, area.height, 0, 0, size, size);
  return new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Crop failed'))), 'image/jpeg', 0.9);
  });
}

/** Square (1:1) cover cropper backed by react-easy-crop; returns a cropped JPEG File. */
export function CoverImageCropper({ file, isOpen, onClose, onCropped, isSaving }: CoverImageCropperProps) {
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [areaPixels, setAreaPixels] = useState<Area | null>(null);

  useEffect(() => {
    if (!file) {
      setImageSrc(null);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setImageSrc(reader.result as string);
    reader.readAsDataURL(file);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, [file]);

  const onCropComplete = useCallback((_area: Area, areaPx: Area) => setAreaPixels(areaPx), []);

  const handleApply = async () => {
    if (!imageSrc || !areaPixels) return;
    const blob = await cropToBlob(imageSrc, areaPixels);
    onCropped(new File([blob], 'cover.jpg', { type: 'image/jpeg' }));
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('playlists.cropCover')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box position="relative" h="320px" bg="gray.900" borderRadius="md" overflow="hidden">
            {imageSrc && (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={1}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            )}
          </Box>
          <HStack mt={4} spacing={3}>
            <Text fontSize="sm" color="gray.500">
              {t('playlists.zoom')}
            </Text>
            <Slider min={1} max={3} step={0.05} value={zoom} onChange={setZoom} flex={1} colorScheme="brand">
              <SliderTrack>
                <SliderFilledTrack />
              </SliderTrack>
              <SliderThumb />
            </Slider>
          </HStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button colorScheme="brand" onClick={handleApply} isLoading={isSaving} isDisabled={!imageSrc}>
            {t('common.save')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default CoverImageCropper;
